import type { Adapter } from './types';
import { registerBuiltInAdapters } from './adapters/registerBuiltInAdapters';

/**
 * Adapter registry keyed by catalog entry `type` (the A2UI component's `component` field). A
 * module-level `Map` mirrors the a2ui-prototype's `registry.ts` convention — adapters are shims
 * registered once at app startup, looked up by `catalogId` dispatch during render.
 */
const registry = new Map<string, Adapter>();

let builtInsRegistered = false;
let loadingBuiltIns = false;

/**
 * Registers the built-in adapters exactly once, on first lookup.
 *
 * `registerBuiltInAdapters` transitively imports the whole organism tree, and that tree imports
 * back into this module (e.g. via `utils/resolveRenderer.ts`), so the static `import` above is
 * circular. The cycle is safe because every registry export is a hoisted function declaration and
 * nothing in the built-in graph calls `registerAdapter`/`hasAdapter`/`getAdapter` at module scope:
 * such a call would run before `registry` is initialised and throw a TDZ `ReferenceError`. Keep
 * every registry call inside a function or component body. The import has to be static — a
 * call-time `require` does not exist under native ESM (Vite, Vitest browser mode). Registration
 * itself waits for the first lookup, by which time every module in the graph has finished
 * evaluating its own top-level code.
 *
 * Re-entry is fatal, not silent. A lookup made while `registerBuiltInAdapters()` is still running
 * (an adapter's registration code asking the registry about another type) would otherwise
 * short-circuit on a half-filled registry and read a real adapter as missing — a wrong answer that
 * surfaces downstream as a spurious `RENDER_ADAPTER_MISSING`, i.e. the plausible-looking finding
 * this whole subsystem exists to avoid producing.
 */
function ensureBuiltIns(): void {
  if (builtInsRegistered) return;
  if (loadingBuiltIns) {
    throw new Error(
      'adapterRegistry: hasAdapter/getAdapter called while the built-in adapters were still registering. ' +
        'Move the lookup out of registration code — resolving it here would read a half-filled registry.'
    );
  }
  loadingBuiltIns = true;
  try {
    // Built-ins must not clobber an adapter a caller registered explicitly: registration is
    // miss-triggered, so an override registered BEFORE the first miss would otherwise be silently
    // replaced by the built-in of the same name the moment some unrelated type missed. Snapshot
    // the explicit registrations and re-apply them over the built-ins.
    const explicit = new Map(registry);
    try {
      registerBuiltInAdapters();
      for (const [type, adapter] of explicit) registry.set(type, adapter);
      builtInsRegistered = true;
    } catch (err) {
      // All-or-nothing. A throw part-way through leaves `builtInsRegistered` false but the
      // already-registered built-ins in the map, so the next `hasAdapter` for one of THOSE answers
      // `true` on the fast path and never retries the backfill — a partially-populated registry
      // read as authoritative, which is the `RENDER_ADAPTER_MISSING` false positive this module is
      // built to rule out. Roll back to the pre-call state so the failure is total and retryable.
      registry.clear();
      for (const [type, adapter] of explicit) registry.set(type, adapter);
      throw err;
    }
  } finally {
    loadingBuiltIns = false;
  }
}

export function registerAdapter(type: string, adapter: Adapter): void {
  registry.set(type, adapter);
}

export function getAdapter(type: string): Adapter | undefined {
  // Miss-triggered, not unconditional: an explicitly `registerAdapter`-ed type (a test double, a
  // deliberate override) is answered without registering the built-ins — some callers
  // (e.g. `composerTryMode.integration.spec.tsx`) register only the handful of adapters a specific
  // document needs and see exactly those as long as they only look up types they registered. A
  // miss on ANY type registers the built-ins (explicit registrations survive it — see
  // `ensureBuiltIns`).
  const existing = registry.get(type);
  if (existing) return existing;
  ensureBuiltIns();
  return registry.get(type);
}

export function hasAdapter(type: string): boolean {
  if (registry.has(type)) return true;
  ensureBuiltIns();
  return registry.has(type);
}

export function listRegisteredTypes(): string[] {
  ensureBuiltIns();
  return Array.from(registry.keys());
}

/** Test-only: resets the registry so specs don't leak adapters across files. */
export function clearAdapterRegistry(): void {
  registry.clear();
  builtInsRegistered = false;
}
